/**
 * Spades — server-authoritative rules engine (partnership, 4 players).
 *
 * Rules implemented (toggles live in {@link SpadesConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). One 52-card deck, 13 each.
 *  - Spades are always trump. Each round every player bids 0..13 tricks, starting left of the dealer.
 *    A bid of 0 is "Nil": the bidder promises to win no tricks at all.
 *  - The player left of the dealer leads the first trick; the trick winner leads the next.
 *  - Players must follow the led suit when able; a void player may play anything.
 *    Spades cannot be led until they are "broken" (a spade was played) unless the leader holds only spades.
 *  - Trick winner: highest spade, else highest card of the led suit.
 *  - Team scoring per round (integers):
 *      contract = sum of the partners' non-nil bids; tricks = all tricks the two partners won.
 *      made   → contract·10 + bags (overtricks); failed → −contract·10.
 *      each nil bidder: won no tricks → +nilBonus, otherwise −nilBonus.
 *    Bags accumulate; every `bagLimit` bags costs `bagPenalty` points.
 *  - The match ends when a team reaches `targetScore` (and leads), falls to `loseScore`, or after `maxRounds`.
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface SpadesConfig {
  /** Match ends when a team reaches this score and leads. */
  targetScore: number;
  /** Match ends when a team falls to this score (negative). */
  loseScore: number;
  /** Hard cap on rounds so a match always terminates. */
  maxRounds: number;
  nilBonus: number;
  /** Bags that trigger a penalty. */
  bagLimit: number;
  bagPenalty: number;
  roundEndDelayMs: number;
  trickEndDelayMs: number;
}

export type SpadesPhase = 'BIDDING' | 'PLAYING' | 'TRICK_END' | 'ROUND_END' | 'MATCH_END';

export interface SpadesTrickPlay {
  seat: number;
  card: CardId;
}

export interface SpadesCompletedTrick {
  leader: number;
  plays: SpadesTrickPlay[];
  winner: number;
}

/** Per-round result for one team (public once the round ends). */
export interface SpadesTeamRound {
  /** Sum of the partners' non-nil bids. */
  contract: number;
  /** Tricks won by both partners together. */
  tricks: number;
  /** Points from the contract (made or failed). */
  contractPoints: number;
  /** Points from nil bids (can be negative). */
  nilPoints: number;
  /** Overtricks added to the bag count this round. */
  bags: number;
  /** Penalty applied this round for reaching the bag limit. */
  bagPenalty: number;
  /** Net score change for the team this round. */
  delta: number;
}

export interface SpadesState {
  config: SpadesConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: SpadesPhase;
  hands: CardId[][];
  bids: (number | null)[];
  tricksWon: number[];
  turn: number;
  trick: SpadesTrickPlay[];
  tricks: SpadesCompletedTrick[];
  spadesBroken: boolean;
  /** Team scores, index = team (seat % 2). */
  teamScores: number[];
  teamBags: number[];
  /** One row per finished round: [team0, team1]. */
  roundResults: SpadesTeamRound[][];
}

export type SpadesAction = { type: 'bid'; value: number } | { type: 'play'; card: CardId };

export type SpadesLegal =
  | { kind: 'bid'; min: number; max: number }
  | { kind: 'play'; cards: CardId[] }
  | { kind: 'none' };

export interface SpadesView {
  gameType: 'SPADES';
  config: SpadesConfig;
  seat: number | null;
  phase: SpadesPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  bids: (number | null)[];
  tricksWon: number[];
  trick: SpadesTrickPlay[];
  lastTrick: SpadesCompletedTrick | null;
  tricks: SpadesCompletedTrick[];
  spadesBroken: boolean;
  teamScores: number[];
  teamBags: number[];
  roundResults: SpadesTeamRound[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: SpadesConfig = {
  targetScore: 500,
  loseScore: -200,
  maxRounds: 12,
  nilBonus: 100,
  bagLimit: 10,
  bagPenalty: 100,
  roundEndDelayMs: 5000,
  trickEndDelayMs: 900,
};

const TRUMP: Suit = 'S';
const SEATS = 4;

export const spadesTeamOf = (seat: number): number => seat % 2;
export const spadesPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export function spadesTrickWinner(plays: readonly SpadesTrickPlay[]): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (c.suit === TRUMP && b.suit !== TRUMP) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === TRUMP || c.suit === lead)) best = p;
  }
  return best.seat;
}

export function spadesLegalCards(hand: readonly CardId[], trick: readonly SpadesTrickPlay[], spadesBroken: boolean): CardId[] {
  const cards = hand.map(parseCard);
  if (trick.length === 0) {
    const nonSpades = cards.filter((c) => c.suit !== TRUMP);
    if (spadesBroken || nonSpades.length === 0) return [...hand];
    return nonSpades.map((c) => c.id);
  }
  const lead = parseCard(trick[0]!.card).suit;
  const followers = cards.filter((c) => c.suit === lead);
  return followers.length > 0 ? followers.map((c) => c.id) : [...hand];
}

export function spadesScoreTeamRound(
  bids: readonly (number | null)[],
  tricksWon: readonly number[],
  team: number,
  bagsBefore: number,
  cfg: SpadesConfig,
): SpadesTeamRound {
  const seats = [team, team + 2];
  let contract = 0;
  let tricks = 0;
  let nilPoints = 0;
  for (const s of seats) {
    const bid = bids[s] ?? 0;
    const won = tricksWon[s] ?? 0;
    tricks += won;
    if (bid === 0) nilPoints += won === 0 ? cfg.nilBonus : -cfg.nilBonus;
    else contract += bid;
  }
  let contractPoints: number;
  let bags = 0;
  if (contract === 0) {
    // Both nil: any tricks are bags too.
    bags = tricks;
    contractPoints = bags;
  } else if (tricks >= contract) {
    bags = tricks - contract;
    contractPoints = contract * 10 + bags;
  } else {
    contractPoints = -contract * 10;
  }
  const bagTotal = bagsBefore + bags;
  const penalties = Math.floor(bagTotal / cfg.bagLimit);
  const bagPenalty = penalties * cfg.bagPenalty;
  return { contract, tricks, contractPoints, nilPoints, bags, bagPenalty, delta: contractPoints + nilPoints - bagPenalty };
}

/** Heuristic bid used for timeouts (bots use a stronger estimator). */
export function spadesSimpleBid(hand: readonly CardId[]): number {
  const cards = hand.map(parseCard);
  const spades = cards.filter((c) => c.suit === TRUMP).length;
  let est = 0;
  for (const c of cards) {
    if (c.suit === TRUMP) est += c.rank === 14 ? 1 : c.rank === 13 ? 0.9 : c.rank === 12 ? 0.6 : 0;
    else if (c.rank === 14) est += 1;
    else if (c.rank === 13 && cards.filter((x) => x.suit === c.suit).length >= 2) est += 0.6;
  }
  if (spades > 4) est += (spades - 4) * 0.8;
  return Math.min(13, Math.max(1, Math.round(est)));
}

function deal(state: SpadesState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.bids = [null, null, null, null];
  state.tricksWon = [0, 0, 0, 0];
  state.trick = [];
  state.tricks = [];
  state.spadesBroken = false;
  state.phase = 'BIDDING';
  state.turn = (state.dealer + 1) % SEATS;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: state.hands.map((h) => h.length) });
}

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: SpadesState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = spadesTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

export const spadesEngine: GameEngine<SpadesConfig, SpadesState, SpadesAction, SpadesView, SpadesLegal> = {
  gameType: 'SPADES',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 50, 2000);
    checkInt('loseScore', c.loseScore, -2000, -1);
    checkInt('maxRounds', c.maxRounds, 1, 30);
    checkInt('nilBonus', c.nilBonus, 0, 500);
    checkInt('bagLimit', c.bagLimit, 2, 20);
    checkInt('bagPenalty', c.bagPenalty, 0, 500);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Spades needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: SpadesState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      bids: [],
      tricksWon: [],
      turn: 0,
      trick: [],
      tricks: [],
      spadesBroken: false,
      teamScores: [0, 0],
      teamBags: [0, 0],
      roundResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'bid' && typeof raw.value === 'number' && Number.isInteger(raw.value)) return { type: 'bid', value: raw.value };
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    throw new EngineError('INVALID_ACTION', 'Unknown Spades action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine(seat === state.turn && (state.phase === 'BIDDING' || state.phase === 'PLAYING'), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    if (action.type === 'bid') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'Bidding is closed');
      assertEngine(action.value >= 0 && action.value <= 13, 'INVALID_AMOUNT', 'Bid out of range');
      state.bids[seat] = action.value;
      events.push({ type: 'bid', seat, value: action.value });
      state.turn = (seat + 1) % SEATS;
      if (state.bids.every((b) => b !== null)) {
        state.phase = 'PLAYING';
        state.turn = (state.dealer + 1) % SEATS;
        events.push({ type: 'biddingComplete', bids: [...state.bids] });
      }
      return events;
    }
    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const hand = state.hands[seat]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(spadesLegalCards(hand, state.trick, state.spadesBroken).includes(action.card), 'ILLEGAL_MOVE', 'That card is not allowed now');
    removeCards(hand, [action.card]);
    state.trick.push({ seat, card: action.card });
    events.push({ type: 'cardPlayed', seat, card: action.card });
    if (parseCard(action.card).suit === TRUMP && !state.spadesBroken) {
      state.spadesBroken = true;
      events.push({ type: 'spadesBroken', seat });
    }
    if (state.trick.length < SEATS) {
      state.turn = (seat + 1) % SEATS;
      return events;
    }
    const winner = spadesTrickWinner(state.trick);
    state.tricksWon[winner]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.turn = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', min: 0, max: 13 };
    if (state.phase === 'PLAYING') return { kind: 'play', cards: spadesLegalCards(state.hands[seat]!, state.trick, state.spadesBroken) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'BIDDING') return { type: 'bid', value: spadesSimpleBid(state.hands[seat]!) };
    const legal = spadesLegalCards(state.hands[seat]!, state.trick, state.spadesBroken);
    // Lowest legal card: rarely wastes a winner, never illegal.
    const lowest = [...legal].sort((a, b) => {
      const ca = parseCard(a);
      const cb = parseCard(b);
      return (ca.suit === TRUMP ? 100 : 0) + ca.rank - ((cb.suit === TRUMP ? 100 : 0) + cb.rank);
    })[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    return {
      gameType: 'SPADES',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      bids: [...state.bids],
      tricksWon: [...state.tricksWon],
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      spadesBroken: state.spadesBroken,
      teamScores: [...state.teamScores],
      teamBags: [...state.teamBags],
      roundResults: state.roundResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[spadesTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.turn });
        return events;
      }
      const cfg = state.config;
      const results = [0, 1].map((team) => spadesScoreTeamRound(state.bids, state.tricksWon, team, state.teamBags[team]!, cfg));
      results.forEach((r, team) => {
        state.teamScores[team]! += r.delta;
        state.teamBags[team] = (state.teamBags[team]! + r.bags) % cfg.bagLimit;
      });
      state.roundResults.push(results);
      events.push({
        type: 'roundEnd',
        round: state.round,
        results,
        teamScores: [...state.teamScores],
        teamBags: [...state.teamBags],
        tricksWon: [...state.tricksWon],
      });
      const [a, b] = state.teamScores as [number, number];
      const reached = a >= cfg.targetScore || b >= cfg.targetScore;
      const busted = a <= cfg.loseScore || b <= cfg.loseScore;
      const capped = state.round >= cfg.maxRounds;
      if ((reached && a !== b) || busted || capped) {
        state.phase = 'MATCH_END';
        events.push({ type: 'matchEnd', standings: teamStandings(state) });
      } else {
        state.phase = 'ROUND_END';
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
      return events;
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
